import { collectRefs } from '../utils/_webComponents';
import { getDataProperty } from '../utils/_domFunctions';
import { getCurrentLocaleName, getDaysOfTheWeek } from '../utils/_i18n';
import { stringifyISODate, parseISODate } from '../utils/_date';

const dateTimeFormats = {
    monthYear: new Intl.DateTimeFormat(getCurrentLocaleName(), { year: 'numeric', month: 'long' })
};

const IdTemplates = {
    title: (idBase) => `${idBase}-title`,
};

const WeekdayLabels = {
    long: getDaysOfTheWeek(undefined, 'long'),
    short: getDaysOfTheWeek(undefined, 'short'),
    narrow: getDaysOfTheWeek(undefined, 'narrow'),
};

class mDatePicker extends HTMLDivElement {
    // TODO: Update ESLint
    static observedAttributes = ['value'];

    constructor() {
        super();

        collectRefs.call(this);

        // Get data props
        this.minDate = parseISODate(getDataProperty(this, 'min', 'string'));
        if (Number.isNaN(this.minDate.valueOf())) {
            this.minDate = null;
        }
        this.maxDate = parseISODate(getDataProperty(this, 'max', 'string'));
        if (Number.isNaN(this.maxDate.valueOf())) {
            this.maxDate = null;
        }
        this.firstDayOfWeek = getDataProperty(this, 'firstDayOfWeek', 'number', 0);

        // Setup focusDay ; today, then correct if out of bounds
        this.focusDay = new Date();
        if (this.maxDate && this.focusDay > this.maxDate) {
            this.focusDay = new Date(this.maxDate);
        }
        if (this.minDate && this.focusDay < this.minDate) {
            this.focusDay = new Date(this.minDate);
        }

        this.lastRow = null;
        this.monthCells = [];
        this.selectedDay = new Date(0, 0, 1);
        this.lastDate = this.focusDay.getDate();

        this.refs.monthYear.id = IdTemplates.title(this.id);

        // Create table grid
        this.refs.tableContainer.innerHTML = '';
        const table = document.createElement('table');
        table.role = 'grid';
        table.setAttribute('aria-labelledby', IdTemplates.title(this.id));

        // Create table head
        const tHead = document.createElement('thead');
        const headRow = document.createElement('tr');
        headRow.className = 'mobile';
        const headRowDesktop = document.createElement('tr');
        headRowDesktop.className = 'desktop';
        for (let i = 0; i < 7; i++) {
            const idx = (this.firstDayOfWeek + i) % 7;
            // Mobile
            const th = document.createElement('th');
            th.setAttribute('scope', 'col');
            th.setAttribute('abbr', WeekdayLabels.long[idx]);
            th.textContent = WeekdayLabels.narrow[idx];
            headRow.appendChild(th);
            // Desktop
            const thd = document.createElement('th');
            thd.setAttribute('scope', 'col');
            thd.setAttribute('abbr', WeekdayLabels.long[idx]);
            thd.textContent = WeekdayLabels.short[idx];
            headRowDesktop.appendChild(thd);
        }
        tHead.appendChild(headRow);
        tHead.appendChild(headRowDesktop);
        table.appendChild(tHead);

        // Create table body
        const tBody = document.createElement('tbody');
        for (let i = 0; i < 6; i++) {
            const row = tBody.insertRow(i);
            this.lastRow = row;
            for (let j = 0; j < 7; j++) {
                const cell = document.createElement('td');
                cell.tabIndex = -1;
                cell.addEventListener('click', this.onDayClick.bind(this));
                cell.addEventListener('keydown', this.onDayKeyDown.bind(this));
                row.appendChild(cell);
                this.monthCells.push(cell);
            }
        }
        table.appendChild(tBody)
        this.refs.tableContainer.appendChild(table);

        this.updateGrid();
        this.setFocusDay();

        // Event handlers
        this.refs.prevMonth.addEventListener('click', this.handlePreviousMonthButton.bind(this));
        this.refs.nextMonth.addEventListener('click', this.handleNextMonthButton.bind(this));
        if (this.refs.earliest) {
            this.refs.earliest.addEventListener('click', this.onClickEarliestButton.bind(this));
        }
    }

    connectedCallback() {}

    disconnectedCallback() {}

    attributeChangedCallback(name, oldValue, newValue) {
        switch (name) {
            case 'value':
                if (newValue) {
                    let newDate = parseISODate(newValue);
                    if (Number.isNaN(newDate.valueOf())) {
                        newDate = new Date();
                    }
                    this.moveFocusToDay(newDate);
                    this.selectedDay = new Date(newDate);
                }
                break;
        }
    }

    isSameDay(day1, day2) {
        return (
            day1.getFullYear() === day2.getFullYear()
            && day1.getMonth() === day2.getMonth()
            && day1.getDate() === day2.getDate()
        );
    }

    getOutOfBoundsDayNum(date) {
        if (this.minDate && date < this.minDate) {
            return Math.round((date - this.minDate) / (1000 * 60 * 60 * 24));
        }
        if (this.maxDate && date > this.maxDate) {
            return Math.round((date - this.maxDate) / (1000 * 60 * 60 * 24));
        }
        return 0;
    }

    updateMonthArrows() {
        if (this.minDate
            && this.minDate.getFullYear() >= this.focusDay.getFullYear()
            && this.minDate.getMonth() >= this.focusDay.getMonth()
        ) {
            this.refs.prevMonth.setAttribute('disabled', '');
        } else {
            this.refs.prevMonth.removeAttribute('disabled');
        }
        if (this.maxDate
            && this.maxDate.getFullYear() <= this.focusDay.getFullYear()
            && this.maxDate.getMonth() <= this.focusDay.getMonth()
        ) {
            this.refs.nextMonth.setAttribute('disabled', '');
        } else {
            this.refs.nextMonth.removeAttribute('disabled');
        }
    }

    updateGrid() {
        // Update month-year heading
        this.refs.monthYear.textContent = dateTimeFormats.monthYear.format(this.focusDay);

        // Update month-year arrows
        this.updateMonthArrows();

        // Update table cells
        const firstDayOfMonth = new Date(this.focusDay.getFullYear(), this.focusDay.getMonth(), 1);
        const dayOfWeek = firstDayOfMonth.getDay();
        firstDayOfMonth.setDate(firstDayOfMonth.getDate() - dayOfWeek + this.firstDayOfWeek);
        const date = new Date(firstDayOfMonth);
        this.monthCells.forEach((cell, i) => {
            const disable = date.getMonth() !== this.focusDay.getMonth();
            const disallow = (this.minDate && date < this.minDate) || (this.maxDate && date > this.maxDate);
            this.updateDate(cell, date, disable, disallow, this.isSameDay(date, this.selectedDay));
            date.setDate(date.getDate() + 1);
            if (i === 35) {
                if (disable) {
                    this.lastRow.style.setProperty('visibility', 'hidden');
                } else {
                    this.lastRow.style.setProperty('visibility', 'visible');
                }
            }
        });
    }

    updateDate(cell, day, disable, disallow, selected) {
        cell.classList.remove('today', 'disabled', 'disallowed');
        cell.removeAttribute('aria-selected');
        cell.tabIndex = -1;

        cell.textContent = day.getDate();
        cell.setAttribute('data-date', stringifyISODate(day));
        if (this.isSameDay(day, new Date())) {
            cell.classList.add('today');
        }
        if (disable) {
            cell.classList.add('disabled');
        }
        if (disallow) {
            cell.classList.add('disallowed');
        }
        if (selected) {
            cell.setAttribute('aria-selected', 'true');
            cell.tabIndex = 0;
        }
    }

    changeMonth(currentDate, numMonths) {
        const isPrev = numMonths < 0;
        const years = Math.trunc(Math.abs(numMonths) / 12);
        const months = Math.abs(numMonths) % 12;
        const newYear = isPrev
            ? currentDate.getFullYear() - years
            : currentDate.getFullYear() + years;
        const newMonth = isPrev
            ? currentDate.getMonth() - months
            : currentDate.getMonth() + months;
        let newDate = new Date(newYear, newMonth, 1);

        const daysInMonth = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0).getDate();
        newDate.setDate(this.lastDate > daysInMonth ? daysInMonth : this.lastDate);
        return newDate;
    }

    moveFocusToDay(day) {
        const oldDay = this.focusDay;
        this.focusDay = day;

        if (
            oldDay.getMonth() !== this.focusDay.getMonth()
            || oldDay.getFullYear() !== this.focusDay.getFullYear()
        ) {
            this.updateGrid();
        }
        this.setFocusDay();
    }

    isDayDisabled(cell) {
        return cell.classList.contains('disabled');
    }

    getDayFromDataDateAttribute(cell) {
        return parseISODate(cell.dataset.date);
    }

    setFocusDay(focus) {
        if (typeof focus !== 'boolean') {
            // eslint-disable-next-line no-param-reassign
            focus = true;
        }

        this.monthCells.forEach((cell) => {
            const day = this.getDayFromDataDateAttribute(cell);
            cell.tabIndex = -1;
            if (this.isSameDay(day, this.focusDay)) {
                cell.tabIndex = 0;
                if (focus) {
                    cell.focus();
                }
            }
        });
    }

    moveToPreviousYear() {
        this.focusDay = this.changeMonth(this.focusDay, -12);
        this.updateGrid();
    }

    moveToNextYear() {
        this.focusDay = this.changeMonth(this.focusDay, 12);
        this.updateGrid();
    }

    moveToPreviousMonth() {
        this.focusDay = this.changeMonth(this.focusDay, -1);
        this.updateGrid();
    }

    moveToNextMonth() {
        this.focusDay = this.changeMonth(this.focusDay, 1);
        this.updateGrid();
    }

    moveFocusToPreviousWeek() {
        const focusDate = new Date(this.focusDay);
        focusDate.setDate(focusDate.getDate() - 7);
        this.lastDate = focusDate.getDate();
        this.moveFocusToDay(focusDate);
    }

    moveFocusToNextWeek() {
        const focusDate = new Date(this.focusDay);
        focusDate.setDate(focusDate.getDate() + 7);
        this.lastDate = focusDate.getDate();
        this.moveFocusToDay(focusDate);
    }

    moveFocusToFirstDayOfWeek() {
        const focusDate = new Date(this.focusDay);
        const offset = (7 + focusDate.getDay() - this.firstDayOfWeek) % 7;
        focusDate.setDate(focusDate.getDate() - offset);
        this.lastDate = focusDate.getDate();
        this.moveFocusToDay(focusDate);
    }

    moveFocusToLastDayOfWeek() {
        const focusDate = new Date(this.focusDay);
        const offset = (6 - focusDate.getDay() + this.firstDayOfWeek) % 7;
        focusDate.setDate(focusDate.getDate() + offset);
        this.lastDate = focusDate.getDate();
        this.moveFocusToDay(focusDate);
    }

    moveFocusToPreviousDay() {
        const focusDate = new Date(this.focusDay);
        focusDate.setDate(focusDate.getDate() - 1);
        this.lastDate = focusDate.getDate();
        this.moveFocusToDay(focusDate);
    }

    moveFocusToNextDay() {
        const focusDate = new Date(this.focusDay);
        focusDate.setDate(focusDate.getDate() + 1);
        this.lastDate = focusDate.getDate();
        this.moveFocusToDay(focusDate);
    }

    selectDay(cellSelected) {
        let dateSelected = this.focusDay;

        if (cellSelected) {
            dateSelected = this.getDayFromDataDateAttribute(cellSelected);
            if (this.getOutOfBoundsDayNum(dateSelected)) {
                return;
            }
            this.focusDay = dateSelected;
            this.monthCells.forEach((cell) => {
                if (cell === cellSelected) {
                    cell.setAttribute('aria-selected', 'true');
                    cell.tabIndex = 0;
                    cell.focus();
                } else {
                    cell.removeAttribute('aria-selected');
                    cell.tabIndex = -1;
                }
            });
        }
        this.selectedDay = dateSelected;
        this.value = stringifyISODate(dateSelected);
        this.dispatchEvent(new Event('change'));
    }

    // Event handlers

    handlePreviousMonthButton(event) {
        let stopEvent = false;

        switch (event.type) {
            case 'keydown':
                switch (event.key) {
                    case 'Enter':
                        this.moveToPreviousMonth();
                        this.setFocusDay(false);
                        stopEvent = true;
                        break;

                    default:
                        break;
                }
                break;

            case 'click':
                this.moveToPreviousMonth();
                this.setFocusDay(false);
                break;

            default:
                break;
        }

        if (stopEvent) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    handleNextMonthButton(event) {
        let stopEvent = false;

        switch (event.type) {
            case 'keydown':
                switch (event.key) {
                    case 'Enter':
                        this.moveToNextMonth();
                        this.setFocusDay(false);
                        stopEvent = true;
                        break;

                    default:
                        break;
                }
                break;

            case 'click':
                this.moveToNextMonth();
                this.setFocusDay(false);
                break;

            default:
                break;
        }

        if (stopEvent) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    onDayClick(event) {
        if (!this.isDayDisabled(event.currentTarget) && event.button !== 2) {
            this.selectDay(event.currentTarget);
        }
        event.preventDefault();
        event.stopPropagation();
    }

    onDayKeyDown(event) {
        let stopEvent = false;

        switch (event.key) {
            // Select day keybind
            case ' ':
            case 'Enter':
                this.selectDay(event.currentTarget);
                stopEvent = true;
                break;

            // Previous day keybind
            case 'ArrowLeft':
                this.moveFocusToPreviousDay();
                stopEvent = true;
                break;

            // Next day keybind
            case 'ArrowRight':
                this.moveFocusToNextDay();
                stopEvent = true;
                break;

            // Previous week keybind
            case 'ArrowUp':
                this.moveFocusToPreviousWeek();
                stopEvent = true;
                break;

            // Next week keybind
            case 'ArrowDown':
                this.moveFocusToNextWeek();
                stopEvent = true;
                break;

            // Previous month/year keybind
            case 'PageUp':
                if (event.shiftKey) {
                    this.moveToPreviousYear();
                } else {
                    this.moveToPreviousMonth();
                }
                this.setFocusDay();
                stopEvent = true;
                break;

            // Next month/year keybind
            case 'PageDown':
                if (event.shiftKey) {
                    this.moveToNextYear();
                } else {
                    this.moveToNextMonth();
                }
                this.setFocusDay();
                stopEvent = true;
                break;

            // Start of the month keybind
            case 'Home':
                this.moveFocusToFirstDayOfWeek();
                stopEvent = true;
                break;

            // End of the month keybind
            case 'End':
                this.moveFocusToLastDayOfWeek();
                stopEvent = true;
                break;

            default:
                break;
        }

        if (stopEvent) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    onClickEarliestButton() {
        this.focusDay = this.minDate || new Date();
        this.selectDay();
        this.updateGrid();
    }
}

customElements.define('m-date-picker', mDatePicker, {
    extends: 'div'
});
